import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Edit, User } from 'lucide-react';
import { useBillParsing } from '@/hooks/useBillParsing';

interface PartiallyParsedCardProps {
  bill: {
    id: string;
    todo_fields?: any; // Json type from DB
    extracted_data?: any;
    workspace_id?: string;
  };
}

export function PartiallyParsedCard({ bill }: PartiallyParsedCardProps) {
  const { triggerParse } = useBillParsing();

  // Parse todo_fields - could be array or JSON
  let todoFields: string[] = [];
  if (Array.isArray(bill.todo_fields)) {
    todoFields = bill.todo_fields;
  } else if (bill.todo_fields && typeof bill.todo_fields === 'object') {
    todoFields = Object.keys(bill.todo_fields);
  }
  
  const extractedData = bill.extracted_data || {};

  // Get successfully extracted fields
  const extractedFields = Object.keys(extractedData).filter(
    key => !todoFields.includes(key)
  );

  const handleSendToManual = () => {
    if (!bill.workspace_id) return;
    
    triggerParse.mutate({
      billIds: [bill.id],
      method: 'manual',
      workspaceId: bill.workspace_id
    });
  };

  return (
    <Alert variant="default" className="border-warning bg-warning/10">
      <AlertTriangle className="h-4 w-4 text-warning" />
      <AlertTitle className="text-warning">Partially Parsed - Action Required</AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          {/* Successfully Extracted */}
          <div>
            <p className="text-sm font-medium mb-2">✓ Successfully Extracted:</p>
            <div className="flex flex-wrap gap-1">
              {extractedFields.length > 0 ? (
                extractedFields.map(field => (
                  <Badge key={field} variant="default" className="text-xs">
                    {field.replace(/_/g, ' ')}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">None</span>
              )}
            </div>
          </div>

          {/* Missing Fields */}
          <div>
            <p className="text-sm font-medium mb-2 text-destructive">✗ Missing Fields:</p>
            <div className="flex flex-wrap gap-1">
              {todoFields.length > 0 ? (
                todoFields.map(field => (
                  <Badge key={field} variant="destructive" className="text-xs">
                    {field.replace(/_/g, ' ')}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">None</span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="default" disabled>
            <Edit className="h-4 w-4 mr-2" />
            Fill Missing Fields
          </Button>
          <Button size="sm" variant="outline" onClick={handleSendToManual}>
            <User className="h-4 w-4 mr-2" />
            Send to Manual Review
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
